/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React from 'react';
import PropTypes from 'prop-types';

import {useProducts} from '@salesforce/commerce-sdk-react';
import ProductTile, {Skeleton} from '@salesforce/retail-react-app/app/components/product-tile';

import {Swiper} from '@amblique/base/app/components/shared/swiper';

/**
 * @param {{
 *  products: string | string[] | object[],
 *  presets: string | string[]
 * }} args
 *
 * @returns {React.ReactElement}
 */
export const ProductSwiper = ({products, presets = '', slideProps, ...props}) => {
  if (typeof products === 'string') {
    products = products.split(/[\n\s,]+/);
  }

  const productIds = products.filter((product) => typeof product === 'string');

  if (productIds.length) {
    const {data: {data: productData} = {}, isLoading} = useProducts({
      parameters: {
        ids: productIds.join(','),
        allImages: true,
        expand: ['availability', 'images', 'links', 'options', 'prices', 'promotions', 'variations'],
        perPricebook: true,
      },
    });

    products = products.map((product, index) => {
      if (typeof product === 'string') {
        return isLoading ?
          {id: product, isLoading} :
          productData && productData.find((productData) => productData.id === product)
        ;
      }

      return product;
    }).filter(Boolean);
  }

  if (!/(^|\s|,)products(\s|,|$)/.test(presets)) {
    presets = `products ${presets}`.trim();
  }

  return (
    <Swiper presets={presets} {...props}>
      {products.map((product) => (
        product.isLoading ?
          <Skeleton key={product.id || product.productId} /> :
          <ProductTile
            key={product.id || product.productId}
            product={product}
            {...(typeof slideProps === 'function' ? slideProps(product) : slideProps)}
          />
      ))}
    </Swiper>
  );
};

ProductSwiper.displayName = 'ProductSwiper';
ProductSwiper.propTypes = {
  ...Swiper.propTypes,
  products: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
};
